import React from 'react';
import { X, Mic, Plus, Clock, ChevronDown, Image, Upload, Trash2, Copy, Check, Filter, Eye, EyeOff, BarChart3 } from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import { getCommissionRate, getTradeCommission, safeTicks } from '../../config';
import { getSpec } from '../../config/futuresSpecs';
import { isAtmArchived } from '../../utils/atmArchive';
import ConfluenceCategoriesAccordion from './parts/ConfluenceCategoriesAccordion';
import { fmtUSD } from '../../utils/formatters';
import { getCurrentTradingDay } from '../../services/tradeProcessor';
import { addDaysToDateStr, getExecutionInstantMs } from '../../services/timeService';
import RichTextEditor from '../UI/RichTextEditor';
import { applyVoiceSubs } from '../../utils/voiceSubs';
import TradeChart from '../Charts/TradeChart';
import { sampleBundleFromTrade } from '../../utils/sampleBars';
import { initBarData, barDataService } from '../../services/barData';
import { computeMaeMfe } from '../../services/barAnalysis/maeMfe';
import { coach, filterMuted, enrichWithMuteAction } from '../../services/coach';
import CoachObservationsPanel from '../Coach/CoachObservationsPanel';
import PreTradeCoachPanel from '../Coach/PreTradeCoachPanel';
import GlassSelect from '../Common/GlassSelect';

// Boot the bar-data service exactly once, on first import. Idempotent —
// re-importing this module elsewhere won't re-register sources.
initBarData();

/**
 * Build the entry/exit marker arrays the chart expects from a trade record.
 * Handles both shapes — and crucially, MIXED shapes: a trade can have a
 * single-leg entry (one fill, lives in trade.entry/entryTime) but a
 * multi-leg exit (scale-out, lives in trade.exits[]). Earlier versions
 * picked one shape OR the other based on whichever side was populated,
 * which dropped the single-leg entry whenever exits were multi-leg.
 *
 * Each side now resolves independently:
 *   - If trade.entries[] has rows with price+quantity → use them
 *   - Else if trade.entry + trade.entryTime are set → synthesize one row
 *   - Same logic mirrored for exits
 */
const buildChartFills = (trade) => {
  if (!trade) return { entries: [], exits: [] };

  const multiEntries = Array.isArray(trade.entries)
    ? trade.entries.filter(e => e?.price && e?.quantity)
    : [];
  const multiExits = Array.isArray(trade.exits)
    ? trade.exits.filter(e => e?.price && e?.quantity)
    : [];

  const totalQty = parseInt(trade.quantity, 10) || 1;

  const entries = multiEntries.length > 0
    ? multiEntries.map(e => ({
        time: e.time,
        price: parseFloat(e.price),
        quantity: parseInt(e.quantity, 10) || 1,
      }))
    : (trade.entry && trade.entryTime
        ? [{
            time: trade.entryTime,
            price: parseFloat(trade.entry),
            quantity: totalQty,
          }]
        : []);

  const exits = multiExits.length > 0
    ? multiExits.map(e => ({
        time: e.time,
        price: parseFloat(e.price),
        quantity: parseInt(e.quantity, 10) || 1,
      }))
    : (trade.exit && trade.exitTime
        ? [{
            time: trade.exitTime,
            price: parseFloat(trade.exit),
            quantity: totalQty,
          }]
        : []);

  return { entries, exits };
};

// Default trade state for initialization
export const DEFAULT_TRADE = {
  // Trade dates anchor to the current trading day so an evening "Add Trade"
  // pre-fills the upcoming session. Callers (TradingJournal) override this
  // when they spread DEFAULT_TRADE, but the export shouldn't ship a wrong default.
  date: getCurrentTradingDay(),
  symbol: '',
  setup: '',
  atmStrategy: '',  // ATM Strategy tracking
  direction: 'long',
  entry: '',
  entryTime: '',  // Entry time (HH:MM format)
  exitTime: '',   // Exit time (HH:MM format)
  exits: [{ quantity: '', price: '', time: '' }],
  stopLoss: '',
  stopLossPoints: '',
  useStopLossPoints: true,  // Default to points mode
  quantity: '',
  pnl: '',
  rMultiple: '',
  tags: [],
  mistakes: [],
  confluences: [],  // Market conditions present during trade (FOR)
  counterConfluences: [],  // Market conditions against the trade
  notes: '',
  accountId: '',
  chartScreenshot: '',  // Base64 encoded chart image
  wickOver: '',  // Wick over/overshoot (for SL calibration)
  useWickOverTicks: true,  // true = ticks, false = points
  // Phase 15 — Confidence Calibration. 1-5 conviction rating set at entry
  // time so the convictionCalibration analyzer can compute per-tier WR
  // and call out tiers that consistently lose. '' (empty string) means
  // "trader didn't rate this trade" and is silently excluded from analysis.
  conviction: '',
  // Phase 16 — Experiment grouping. Free-text label (e.g., "SL Test 5/15")
  // marking trades that are part of a deliberate A/B test. Variants share
  // the same `experiment` value but typically differ in atmStrategy or other
  // params. The experimentComparison analyzer compares variants within an
  // experiment so Coach can call out the winning variant by R/WR. '' means
  // "not part of an experiment" and the trade is treated normally.
  experiment: ''
};

const AddEditTradeModal = ({
  darkMode,
  isEditing,
  trade,
  setTrade,
  accounts,
  setups,
  setupsWithDetails = [],
  eventTags,
  mistakes,
  confluences = [],
  futuresSpecs,
  // v7.6.0 archive system — used to annotate archived ATMs in the dropdown
  // with "(archived)" so the trader knows a template no longer exists in NT.
  // Optional: when not provided, the dropdown falls back to plain names.
  ntTemplateVersions = {},
  voiceInput,
  setVoiceInput,
  parseNaturalLanguageTrade,
  onClose,
  onSave,
  getRemainingContracts,
  addExit,
  removeExit,
  handleTagToggle,
  handleMistakeToggle,
  handleConfluenceToggle,
  handleCounterConfluenceToggle,
  matchAtmToSetup,
  trades = [],
  onBulkCopyConfluences,
  onWickOverUnitChange,
  voiceSubstitutions = [],
  // User's chart preferences (timeframe defaults, EMA periods/colors).
  // When omitted, TradeChart falls back to its own DEFAULT_EMA_PERIODS.
  chartSettings = null,
  // Coach context — when provided, the modal renders per-trade observations
  // alongside the chart. mutedObservations filters out silenced narratives;
  // onCoachAction dispatches Pin/Tag/Lesson/Mute commands on the cards.
  mutedObservations = {},
  onCoachAction,
  // Pre-Trade Coach context — when supplied, the panel renders at the
  // top of the modal and surfaces historical context for the in-progress
  // trade. setupDetails powers the recommended-confluence check;
  // allConfluences resolves chip labels; getTradeNetPnL keeps WR
  // commission-aware. All are optional — panel quietly omits sections
  // it doesn't have data for.
  setupDetails = {},
  allConfluences = [],
  getTradeNetPnL,
  getAccountById,
  onJumpToTrade,
}) => {
  // Account privacy toggle
  const [accountHidden, setAccountHidden] = useState(false);

  // Right column view: 'chart' (Lightweight Charts with markers) or 'screenshot' (legacy upload)
  // Default to chart when bars are available; user can flip to screenshot if they prefer
  // to attach an external image (full TradingView snapshot, multi-pane chart, etc).
  const [rightColView, setRightColView] = useState('chart');
  const [chartTimeframe, setChartTimeframe] = useState('5m');

  // Viewport-aware chart height. The modal's right column previously locked
  // the chart canvas at 440px, which left a lot of dead space on tall
  // monitors. We compute a height that grows with the window (capped at
  // ~720px so it doesn't push the source badge / MAE-MFE row off-screen on
  // shorter windows), and listen for resize so dragging the window adjusts
  // the chart live. Floor is 440 (the original) so short screens never get
  // a worse experience than before.
  const computeChartHeight = () => {
    if (typeof window === 'undefined') return 440;
    // ~280px is the budget for the modal's surrounding chrome (header,
    // chart-header row, replay bar, source badge, padding). Tune if the
    // chart's bottom edge stops aligning with the modal scroll body.
    return Math.max(440, Math.min(720, window.innerHeight - 280));
  };
  const [chartHeight, setChartHeight] = useState(computeChartHeight);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setChartHeight(computeChartHeight());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Build the chart inputs from the current trade. Bars come from the
  // BarDataService (Phase B) which walks registered sources by priority
  // and caches results. If no source has bars for this trade, we fall back
  // to the sample generator so the chart is never empty during dev/testing.
  // The fallback can be turned off in Phase D once a real source is reliable.
  // We list the specific trade fields buildChartFills reads instead of
  // depending on the whole `trade` object — keeps the memo from invalidating
  // when unrelated fields (notes, tags, etc.) change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const chartFills = useMemo(() => buildChartFills(trade), [
    trade?.entries, trade?.exits, trade?.entry, trade?.exit,
    trade?.entryTime, trade?.exitTime, trade?.quantity
  ]);

  const [chartBars, setChartBars] = useState([]);
  const [chartBarsMeta, setChartBarsMeta] = useState({ source: null, fromCache: false, fallback: false });
  // Live-refresh tick — bumps on a timer when the trade is on today's session.
  // The chart fetch effect depends on this so it re-runs every TTL window,
  // pulling new bars as JournifiBars (or whichever live source) appends them.
  // Past-day trades skip the timer entirely (their bars are immutable).
  const [chartRefreshTick, setChartRefreshTick] = useState(0);
  // True between bumping the tick and the resulting fetch landing — drives
  // the spinning Refresh icon in the chart toolbar so the user gets feedback
  // for "I clicked refresh, something is happening."
  const [chartRefreshing, setChartRefreshing] = useState(false);
  useEffect(() => {
    if (!trade?.date) return;
    let isLive = false;
    try { isLive = trade.date === getCurrentTradingDay(); } catch { /* */ }
    if (!isLive) return;
    // 60s matches the cache TTL in BarDataService — re-fetching more often
    // would bypass nothing (cache would still hand us the same bars). Less
    // often and the user sees stale bars longer than necessary.
    const id = setInterval(() => setChartRefreshTick(n => n + 1), 60 * 1000);
    return () => clearInterval(id);
  }, [trade?.date]);
  // Manual refresh handler exposed to the chart toolbar. Bumps the tick
  // (which the fetch effect re-runs on, with skipCache=true since tick > 0).
  const handleManualChartRefresh = React.useCallback(() => {
    setChartRefreshing(true);
    setChartRefreshTick(n => n + 1);
  }, []);
  // 1m bars used exclusively for the MAE/MFE walk. Decoupled from the
  // chart-display TF so short-duration trades (e.g. 3-minute stop-outs)
  // get accurate measurement regardless of what TF the user is viewing.
  // Falls back to chartBars if 1m isn't available from any source.
  const [analysisBars, setAnalysisBars] = useState([]);
  // HTF bars for stack-context capture. Same (symbol, date) as the entry-TF
  // bars but at higher timeframes (15m / 1h / 4h by default). Fed through
  // computeMaeMfe so `barAnalysis.htfEmas` lands on the trade and the Coach
  // can ask "did this entry agree with the bigger trend?". Tiny incremental
  // cost — the BarCache absorbs repeat hits across trades on the same day.
  const [htfBars, setHtfBars] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!trade?.symbol || !trade?.date) {
      setChartBars([]);
      setAnalysisBars([]);
      setChartBarsMeta({ source: null, fromCache: false, fallback: false });
      setHtfBars(null);
      return;
    }
    const htfTimeframes = chartSettings?.htfTimeframes ?? ['15m', '1h', '4h'];
    // Initial mount uses cache (instant chart open); each subsequent tick
    // forces a cache bypass so the chart reflects whatever JournifiBars
    // most recently flushed to disk. Without this, the in-modal refresh
    // would always read the same cached entry until the TTL boundary —
    // i.e. always one tick behind during a live session.
    const skipCache = chartRefreshTick > 0;
    (async () => {
      try {
        // Wave E.3 / v7.5.1 — also fetch the PRIOR trading day's bars so the
        // chart shows yesterday's tail (structural context for Golden Crayon,
        // PDH/PDL/PDC formation) AND so the analysis pack has multi-day data
        // available. Without the prior-day bars, computeAvwapPack and
        // computeKeyLevels can't populate any prior-day-* fields → Golden
        // Crayon, PDH/PDL/PDC AVWAPs all silently return null. This is the
        // prerequisite bugfix for the Wave D + E.2 features to actually fire.
        //
        // Holiday-aware prior-day resolution: try -1, -2, -3 in parallel
        // and take the most-recent non-empty result. Covers weekends
        // (Mon → Fri = -3) and 1-day federal holidays without hardcoding
        // an exchange calendar.
        const PRIOR_ATTEMPTS = [1, 2, 3];
        const fetchPriorDay = async (tf) => {
          const results = await Promise.all(PRIOR_ATTEMPTS.map(n => {
            const pd = addDaysToDateStr(trade.date, -n);
            return barDataService.fetchBars(
              { symbol: trade.symbol, dateStr: pd, timeframe: tf },
              { skipCache }
            ).catch(() => ({ bars: [] }));
          }));
          // Take the first attempt (smallest n = most recent prior day)
          // that returned bars. Sequential preference, not parallel race.
          for (const res of results) {
            if (Array.isArray(res?.bars) && res.bars.length > 0) return res.bars;
          }
          return [];
        };

        // Fetch chart-TF (display) + 1m (analysis) + every HTF in parallel.
        // Each fetch is paired with its prior-day counterpart so we get
        // multi-day coverage uniformly. The BarCache absorbs repeat hits,
        // so this costs N parallel reads first time and ~0 thereafter. If
        // chartTimeframe IS 1m we skip the separate 1m fetch and reuse it.
        const wantSeparateAnalysisFetch = chartTimeframe !== '1m';
        const [
          chartRes, chartPriorBars,
          analysisRes, analysisPriorBars,
          ...htfResults
        ] = await Promise.all([
          barDataService.fetchBars({ symbol: trade.symbol, dateStr: trade.date, timeframe: chartTimeframe }, { skipCache }),
          fetchPriorDay(chartTimeframe),
          wantSeparateAnalysisFetch
            ? barDataService.fetchBars({ symbol: trade.symbol, dateStr: trade.date, timeframe: '1m' }, { skipCache })
                .catch(() => ({ bars: [] }))
            : Promise.resolve(null),
          wantSeparateAnalysisFetch
            ? fetchPriorDay('1m')
            : Promise.resolve([]),
          ...htfTimeframes
            .filter(tf => tf !== chartTimeframe && tf !== '1m')
            .flatMap(tf => [
              barDataService.fetchBars({ symbol: trade.symbol, dateStr: trade.date, timeframe: tf }, { skipCache })
                .then(res => ({ tf, kind: 'today', bars: Array.isArray(res?.bars) ? res.bars : [] }))
                .catch(() => ({ tf, kind: 'today', bars: [] })),
              fetchPriorDay(tf)
                .then(bars => ({ tf, kind: 'prior', bars })),
            ]),
        ]);
        if (cancelled) return;

        // Pack HTF results into a TF-keyed map. Each TF has both 'today' and
        // 'prior' entries from the flatMap above; concatenate to a single
        // chronological array per TF.
        const htfMap = {};
        for (const r of htfResults) {
          if (!htfMap[r.tf]) htfMap[r.tf] = [];
          if (r.kind === 'prior') htfMap[r.tf] = [...r.bars, ...htfMap[r.tf]];
          else htfMap[r.tf] = [...htfMap[r.tf], ...r.bars];
        }
        // Drop any TF that ended up entirely empty.
        const cleanedHtfMap = Object.fromEntries(
          Object.entries(htfMap).filter(([, bars]) => bars.length > 0)
        );
        setHtfBars(Object.keys(cleanedHtfMap).length > 0 ? cleanedHtfMap : null);

        if (Array.isArray(chartRes?.bars) && chartRes.bars.length > 0) {
          // Chart bars: prior + today, in chronological order. The Lightweight
          // Charts auto-zoom still centers on the trade entry, so the default
          // view is unchanged — but scrolling left now reveals yesterday.
          const mergedChartBars = [...chartPriorBars, ...chartRes.bars];
          setChartBars(mergedChartBars);
          setChartBarsMeta({ source: chartRes.source, fromCache: chartRes.fromCache, fallback: false });
          // Analysis bars: prefer the dedicated 1m fetch when it returned
          // data; otherwise reuse the merged chart bars. Either way include
          // the prior day so computeAvwapPack / computeKeyLevels can populate
          // prior-day-* fields.
          const analysisToday = wantSeparateAnalysisFetch && Array.isArray(analysisRes?.bars) && analysisRes.bars.length > 0
            ? analysisRes.bars
            : chartRes.bars;
          const analysisPrior = wantSeparateAnalysisFetch && analysisPriorBars.length > 0
            ? analysisPriorBars
            : chartPriorBars;
          const mergedAnalysis = [...analysisPrior, ...analysisToday];
          setAnalysisBars(mergedAnalysis);
          return;
        }
      } catch (e) {
        if (!cancelled) console.warn('[Chart] BarDataService fetch failed:', e);
      }
      // Fallback: sample bars so the chart visual stays useful pre-Phase-C
      if (cancelled) return;
      const { bars } = sampleBundleFromTrade({ ...trade }, chartTimeframe) || { bars: [] };
      setChartBars(bars);
      setAnalysisBars(bars);  // sample bars are flagged as fallback below; analysis won't run
      setChartBarsMeta({ source: 'sample (no real data found)', fromCache: false, fallback: true });
    })().finally(() => {
      // Clear the in-flight flag whether the fetch succeeded, fell to the
      // sample fallback, or threw. The chart toolbar's refresh icon stops
      // spinning either way — important because a failure with the icon
      // still spinning would feel stuck.
      if (!cancelled) setChartRefreshing(false);
    });
    return () => { cancelled = true; };
    // chartRefreshTick is intentionally a dep — when it bumps (every 60s
    // for live-day trades, OR when the user clicks the toolbar refresh)
    // the effect re-runs with skipCache=true, pulling fresh bars from
    // JournifiBars.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trade?.symbol, trade?.date, trade?.id, chartTimeframe, chartRefreshTick]);
  const chartStopLoss = useMemo(() => {
    const v = parseFloat(trade?.stopLoss);
    return Number.isFinite(v) && v > 0 ? v : undefined;
  }, [trade?.stopLoss]);
  const chartTakeProfit = useMemo(() => {
    const v = parseFloat(trade?.takeProfit);
    return Number.isFinite(v) && v > 0 ? v : undefined;
  }, [trade?.takeProfit]);

  // ── Auto-derive MAE/MFE from bars ─────────────────────────────────────
  // When real bars are loaded for this trade, compute MAE/MFE and surface
  // it both in the UI (below the chart) and on the trade record so the
  // Coach analyzers can read it. The trade.mae / trade.mfe fields stay
  // backwards-compatible with broker imports — we only backfill them when
  // empty (broker values, when present, are authoritative).
  const barAnalysis = useMemo(() => {
    // Use the dedicated 1m analysis fetch (or the chart bars when 1m fell
    // back to chart-TF). Sample/fallback bars never count.
    const walkBars = analysisBars && analysisBars.length > 0 ? analysisBars : chartBars;
    if (!walkBars || walkBars.length === 0) return null;
    if (chartBarsMeta?.fallback) return null;
    if (!trade?.date || !trade?.entryTime) return null;
    if (!Number.isFinite(parseFloat(trade?.entry))) return null;
    // Snapshot EMAs at the user's configured periods. computeMaeMfe falls
    // back to its own DEFAULT_EMA_PERIODS when emaPeriods is undefined,
    // so omitting it for users who haven't customized works correctly.
    return computeMaeMfe(trade, walkBars, {
      emaPeriods: chartSettings?.emaPeriods,
      htfBars: htfBars || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    analysisBars, chartBars, chartBarsMeta?.fallback, htfBars,
    trade?.date, trade?.entryTime, trade?.exitTime,
    trade?.entry, trade?.direction, trade?.stopLoss, trade?.useStopLossPoints,
    trade?.stopLossPoints, trade?.symbol, trade?.quantity,
    chartSettings?.emaPeriods,
  ]);

  // Per-trade Coach observations — narratives the Coach has about THIS
  // trade specifically (A-grade, wicked-out, round-trip, etc.). Computed
  // against an in-memory trade snapshot that includes the freshly-derived
  // barAnalysis (the persisted trade.barAnalysis is pushed via setTrade
  // separately and may lag a render). Filtered through the user's mute
  // map and enriched with a Mute action so the trader can silence
  // narratives they've already addressed.
  const tradeObservations = useMemo(() => {
    if (!barAnalysis) return [];
    const tradeWithBa = { ...trade, barAnalysis };
    const raw = coach.analyze({ type: 'trade', id: trade?.id }, { trade: tradeWithBa, tier: 'pro' });
    return enrichWithMuteAction(filterMuted(raw, mutedObservations));
  }, [barAnalysis, trade, mutedObservations]);

  // Push computed barAnalysis up to the in-memory trade so:
  //   1. The Coach sees it (existing analyzers read trade.mae / trade.mfe)
  //   2. It saves with the trade on the user's next Save action
  // Guarded by a value comparison to prevent the
  //   computeMaeMfe → setTrade → re-render → recompute (new computedAt) → setTrade
  // loop. Compare only the data fields, ignoring computedAt's tick.
  useEffect(() => {
    if (!barAnalysis || typeof setTrade !== 'function') return;
    const existing = trade?.barAnalysis;
    // htfEmas may settle later than the entry-TF bars (the modal kicks off
    // both fetches in parallel). Compare the keyset so a delayed HTF arrival
    // still triggers a re-push, even if maePoints/mfePoints didn't change.
    const sameHtfShape = (() => {
      const a = existing?.htfEmas;
      const b = barAnalysis.htfEmas;
      if (!a && !b) return true;
      if (!a || !b) return false;
      const ak = Object.keys(a).sort().join(',');
      const bk = Object.keys(b).sort().join(',');
      return ak === bk;
    })();
    const sameValues = existing
      && existing.maePoints === barAnalysis.maePoints
      && existing.mfePoints === barAnalysis.mfePoints
      && existing.barsConsidered === barAnalysis.barsConsidered
      && existing.source === barAnalysis.source
      && sameHtfShape;
    if (sameValues) return;

    const update = { ...trade, barAnalysis };
    // Only backfill broker-style MAE/MFE (dollars) when the trade has
    // none. Trades imported from NT or other platforms that include MAE/MFE
    // keep their broker-truth values untouched.
    if ((trade.mae == null || trade.mae === '') && barAnalysis.maeDollars != null) {
      update.mae = barAnalysis.maeDollars;
    }
    if ((trade.mfe == null || trade.mfe === '') && barAnalysis.mfeDollars != null) {
      update.mfe = barAnalysis.mfeDollars;
    }
    setTrade(update);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barAnalysis]);

  // ATM Strategy dropdown state
  const [showAtmDropdown, setShowAtmDropdown] = useState(false);
  const [atmSuggestion, setAtmSuggestion] = useState(null); // { setupName: string } when we have a suggestion

  // Bulk copy confluences state
  const [showBulkCopy, setShowBulkCopy] = useState(false);
  const [bulkCopyMode, setBulkCopyMode] = useState('add'); // 'add' | 'replace'
  const [bulkCopySelectedTrades, setBulkCopySelectedTrades] = useState([]);
  const [bulkCopyDateFilter, setBulkCopyDateFilter] = useState('today'); // 'today' | 'week' | 'month' | 'all'
  const [bulkCopySymbolFilter, setBulkCopySymbolFilter] = useState('all'); // 'all' | specific symbol

  // Counter-confluences (Against Trade) collapse state
  const [showCounterConfluences, setShowCounterConfluences] = useState(
    () => (trade?.counterConfluences || []).length > 0
  );

  // Confluence matcher state
  const [confluenceMatcherInput, setConfluenceMatcherInput] = useState('');
  const [confluenceMatcherFeedback, setConfluenceMatcherFeedback] = useState(null); // { matched: [], notFound: [] }
  const [isListening, setIsListening] = useState(false);
  const confluenceInputRef = React.useRef(null);
  // Voice-typing timers — captured in refs so unmount cleanup can clear them.
  // Without this, closing the modal during the 15s safety window left an
  // interval and a setTimeout firing on an unmounted component.
  const voicePollRef = React.useRef(null);
  const voiceSafetyRef = React.useRef(null);
  React.useEffect(() => () => {
    if (voicePollRef.current) clearInterval(voicePollRef.current);
    if (voiceSafetyRef.current) clearTimeout(voiceSafetyRef.current);
  }, []);

  // Confluence matcher helpers (shared by preview + commit)
  const ordinalToNum = useMemo(() => ({ first: '1st', second: '2nd', third: '3rd', fourth: '4th', fifth: '5th', sixth: '6th', seventh: '7th', eighth: '8th', ninth: '9th', tenth: '10th' }), []);
  const numToOrdinal = useMemo(() => Object.fromEntries(Object.entries(ordinalToNum).map(([k, v]) => [v, k])), [ordinalToNum]);
  const normalize = React.useCallback((s) => applyVoiceSubs(s, voiceSubstitutions).toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\b0+(\d)/g, '$1').trim(), [voiceSubstitutions]);
  const getAlternate = React.useCallback((s) => {
    let alt = s;
    for (const [word, num] of Object.entries(ordinalToNum)) {
      alt = alt.replace(new RegExp(`\\b${word}\\b`, 'g'), num);
    }
    if (alt === s) {
      for (const [num, word] of Object.entries(numToOrdinal)) {
        alt = alt.replace(new RegExp(`\\b${num}\\b`, 'g'), word);
      }
    }
    return alt === s ? null : alt;
  }, [ordinalToNum, numToOrdinal]);

  // Match a single term against confluences (no side effects)
  const matchTerm = React.useCallback((raw) => {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const symbolPrefix = trimmed.startsWith('!') || trimmed.startsWith('-');
    const normalizedRaw = normalize(trimmed);
    const wordPrefix = normalizedRaw.startsWith('against ') || normalizedRaw.startsWith('not ');
    const isAgainst = symbolPrefix || wordPrefix;
    const term = symbolPrefix
      ? normalize(trimmed.slice(1))
      : wordPrefix
        ? normalizedRaw.replace(/^(against|not)\s+/, '')
        : normalizedRaw;
    if (!term) return null;

    const termAlt = getAlternate(term);
    const termsToTry = termAlt ? [term, termAlt] : [term];
    let bestMatch = null;
    let bestScore = 0;

    for (const conf of confluences) {
      const confName = normalize(conf.name);
      const confAlt = getAlternate(confName);
      const confNames = confAlt ? [confName, confAlt] : [confName];
      let score = 0;
      for (const t of termsToTry) {
        const tWords = t.split(/\s+/);
        const tCompressed = t.replace(/\s+/g, '');
        for (const cn of confNames) {
          const cnWords = cn.split(/\s+/);
          const cnCompressed = cn.replace(/\s+/g, '');
          let s = 0;
          const lenRatio = t.length / Math.max(cn.length, 1);
          // Check if substring match starts at a word boundary (not mid-word like "5m" in "15m")
          const atBoundary = (str, sub) => { const i = str.indexOf(sub); return i === 0 || /[\s-]/.test(str[i - 1]); };
          if (cn === t) { s = 100; }
          else if (tCompressed === cnCompressed) { s = 95; }
          else if (cn.includes(t) && atBoundary(cn, t)) { s = 70 + Math.round(20 * lenRatio); }
          else if (cn.includes(t)) { s = 35 + Math.round(15 * lenRatio); }
          else if (cnCompressed.includes(tCompressed)) { s = 30 + Math.round(15 * lenRatio); }
          else if (t.includes(cn) && atBoundary(t, cn)) { s = 50 + Math.round(20 * lenRatio); }
          else if (t.includes(cn)) { s = 25 + Math.round(15 * lenRatio); }
          else if (tCompressed.includes(cnCompressed)) { s = 20 + Math.round(15 * lenRatio); }
          else {
            const mw = tWords.reduce((acc, tw) => {
              if (cnWords.some(cw => cw === tw)) return acc + 1.0;
              if (cnWords.some(cw => cw.startsWith(tw) || tw.startsWith(cw))) return acc + 0.7;
              if (cnWords.some(cw => cw.includes(tw) || tw.includes(cw))) return acc + 0.3;
              return acc;
            }, 0);
            if (mw > 0) s = Math.round((mw / Math.max(tWords.length, cnWords.length)) * 70);
          }
          if (s > score) score = s;
        }
      }
      if (score > bestScore) { bestScore = score; bestMatch = conf; }
    }

    if (bestMatch && bestScore >= 40) {
      return { term: raw, name: bestMatch.name, id: bestMatch.id, isAgainst, score: bestScore };
    }
    return { term: raw, name: null, id: null, isAgainst, score: 0 };
  }, [confluences, normalize, getAlternate]);

  // Live preview: re-match on every input change (only completed terms after commas)
  const [matchPreview, setMatchPreview] = useState([]); // [{ term, name, id, isAgainst, score }]
  React.useEffect(() => {
    const terms = confluenceMatcherInput.split(',').map(t => t.trim()).filter(Boolean);
    if (!terms.length) { setMatchPreview([]); return; }
    // Only preview terms that are "complete" (followed by a comma) — last term is still being typed
    const hasTrailingComma = confluenceMatcherInput.trimEnd().endsWith(',');
    const completedTerms = hasTrailingComma ? terms : terms.slice(0, -1);
    if (!completedTerms.length) { setMatchPreview([]); return; }
    setMatchPreview(completedTerms.map(matchTerm).filter(Boolean));
  }, [confluenceMatcherInput, matchTerm]);

  // Commit: apply all matches (preview + any trailing term) and toggle confluences
  const handleConfluenceMatch = () => {
    if (!confluenceMatcherInput.trim()) return;
    const terms = confluenceMatcherInput.split(',').map(t => t.trim()).filter(Boolean);
    const matched = [];
    const notFound = [];

    for (const raw of terms) {
      const result = matchTerm(raw);
      if (!result) continue;
      if (result.name) {
        if (result.isAgainst) {
          handleCounterConfluenceToggle(result.id);
        } else {
          handleConfluenceToggle(result.id);
        }
        matched.push(result);
      } else {
        notFound.push(raw);
      }
    }

    setConfluenceMatcherFeedback({ matched, notFound });
    setConfluenceMatcherInput('');
    setMatchPreview([]);
    setTimeout(() => setConfluenceMatcherFeedback(null), 5000);
  };

  // Trigger Windows Voice Typing (Win+H) then auto-clean text with voice substitutions
  const toggleVoiceInput = async () => {
    // Focus the input first so Windows Voice Typing targets it
    if (confluenceInputRef.current) {
      confluenceInputRef.current.focus();
    }

    if (window.electronAPI?.triggerVoiceTyping) {
      // Snapshot what's already in the input before voice starts
      const beforeVoice = confluenceMatcherInput;
      setIsListening(true);
      const ok = await window.electronAPI.triggerVoiceTyping();

      if (!ok) {
        setIsListening(false);
        setConfluenceMatcherFeedback({ matched: [], notFound: ['Failed to open Voice Typing — try Win+H manually'] });
        setTimeout(() => setConfluenceMatcherFeedback(null), 3000);
        return;
      }

      // Clear any prior in-flight voice timers (rapid double-clicks of the
      // mic button shouldn't stack)
      if (voicePollRef.current) clearInterval(voicePollRef.current);
      if (voiceSafetyRef.current) clearTimeout(voiceSafetyRef.current);

      // After Win+H opens, wait for user to finish speaking then apply voiceSubs.
      // We poll the input for changes — once it stabilizes (no new text for 800ms), clean it up.
      let lastValue = beforeVoice;
      let stableCount = 0;
      voicePollRef.current = setInterval(() => {
        const current = confluenceInputRef.current?.value || '';
        if (current !== lastValue) {
          // Still receiving text from voice typing
          lastValue = current;
          stableCount = 0;
        } else if (current !== beforeVoice) {
          // Text has stabilized and is different from before voice started
          stableCount++;
          if (stableCount >= 3) {
            // ~1.2s of no changes — apply voice substitutions
            if (voicePollRef.current) clearInterval(voicePollRef.current);
            voicePollRef.current = null;
            if (voiceSafetyRef.current) clearTimeout(voiceSafetyRef.current);
            voiceSafetyRef.current = null;
            const cleaned = applyVoiceSubs(current, voiceSubstitutions);
            if (cleaned !== current) {
              setConfluenceMatcherInput(cleaned);
            }
            setIsListening(false);
          }
        } else {
          // No text received yet, keep waiting (up to the timeout below)
        }
      }, 400);

      // Safety timeout: stop polling after 15 seconds regardless
      voiceSafetyRef.current = setTimeout(() => {
        if (voicePollRef.current) clearInterval(voicePollRef.current);
        voicePollRef.current = null;
        voiceSafetyRef.current = null;
        // Apply voiceSubs to whatever is there
        const current = confluenceInputRef.current?.value || confluenceMatcherInput;
        if (current !== beforeVoice) {
          const cleaned = applyVoiceSubs(current, voiceSubstitutions);
          if (cleaned !== current) {
            setConfluenceMatcherInput(cleaned);
          }
        }
        setIsListening(false);
      }, 15000);
    } else {
      setConfluenceMatcherFeedback({ matched: [], notFound: ['Press Win+H to activate Windows Voice Typing'] });
      setTimeout(() => setConfluenceMatcherFeedback(null), 4000);
    }
  };

  // Get unique ATM strategies from existing trades for dropdown
  const existingAtmStrategies = useMemo(() => {
    const atms = trades
      .map(t => t.atmStrategy)
      .filter(atm => atm && atm.trim())
      .reduce((acc, atm) => {
        acc[atm] = (acc[atm] || 0) + 1;
        return acc;
      }, {});
    // Sort by frequency (most used first)
    return Object.entries(atms)
      .sort((a, b) => b[1] - a[1])
      .map(([atm]) => atm);
  }, [trades]);
  
  // Handle ATM change with pattern matching suggestion
  const handleAtmChange = (newAtmValue) => {
    setTrade({...trade, atmStrategy: newAtmValue});
    setShowAtmDropdown(false);
    
    // Check if this ATM matches a pattern
    if (matchAtmToSetup && newAtmValue) {
      const matchedSetup = matchAtmToSetup(newAtmValue);
      if (matchedSetup && matchedSetup !== trade.setup) {
        // Show suggestion prompt
        setAtmSuggestion({ setupName: matchedSetup });
      } else {
        setAtmSuggestion(null);
      }
    }
  };
  
  // Apply suggested setup
  const applySuggestedSetup = () => {
    if (atmSuggestion) {
      setTrade({...trade, setup: atmSuggestion.setupName});
      setAtmSuggestion(null);
    }
  };
  
  // Dismiss suggestion
  const dismissSuggestion = () => {
    setAtmSuggestion(null);
  };
  
  // Calculate P&L preview for an exit (handles direction properly)
  const calculateExitPnL = (exit, tradeData = trade) => {
    const entry = parseFloat(tradeData.entry);
    const exitPrice = parseFloat(exit.price);
    const qty = parseInt(exit.quantity);
    const symbol = tradeData.symbol?.toUpperCase();
    
    const spec = getSpec(symbol);
    if (!entry || !exitPrice || !qty || !symbol || !spec) {
      return 0;
    }
    // Handle both 'short' and 'Short' (case-insensitive)
    const isShort = tradeData.direction?.toLowerCase() === 'short';
    const direction = isShort ? -1 : 1;
    const points = (exitPrice - entry) * direction;
    const ticks = safeTicks(points, spec.tickSize);
    return ticks * spec.tickValue * qty;
  };

  // Calculate R-Multiple for a single exit
  const calculateExitRMultiple = (exit, tradeData = trade) => {
    const pnl = calculateExitPnL(exit, tradeData);
    const qty = parseInt(exit.quantity);
    const symbol = tradeData.symbol?.toUpperCase();
    
    const spec = getSpec(symbol);
    if (!pnl || !qty || !symbol || !spec) {
      return null;
    }

    // Get stop loss in points
    let stopLossPoints = 0;
    if (tradeData.useStopLossPoints && tradeData.stopLossPoints) {
      stopLossPoints = parseFloat(tradeData.stopLossPoints);
    } else if (tradeData.stopLoss && tradeData.entry) {
      stopLossPoints = Math.abs(parseFloat(tradeData.stopLoss) - parseFloat(tradeData.entry));
    }

    if (stopLossPoints <= 0) {
      return null;
    }
    const riskTicks = safeTicks(stopLossPoints, spec.tickSize);
    const riskDollars = riskTicks * spec.tickValue * qty;
    
    if (riskDollars <= 0) {
      return null;
    }
    
    return pnl / riskDollars;
  };

  // Auto-recalculate P&L and R-Multiple
  const recalculateAll = (updates = {}) => {
    const updatedTrade = { ...trade, ...updates };
    
    // Calculate total P&L from exits using the shared function
    const totalPnL = (updatedTrade.exits || []).reduce((sum, exit) => {
      return sum + calculateExitPnL(exit, updatedTrade);
    }, 0);
    
    // Calculate R-Multiple if we have stop loss
    let rMultiple = '';
    let stopLossPoints = 0;
    if (updatedTrade.useStopLossPoints && updatedTrade.stopLossPoints) {
      stopLossPoints = parseFloat(updatedTrade.stopLossPoints);
    } else if (updatedTrade.stopLoss && updatedTrade.entry) {
      stopLossPoints = Math.abs(parseFloat(updatedTrade.stopLoss) - parseFloat(updatedTrade.entry));
    }
    
    if (stopLossPoints > 0 && totalPnL !== 0) {
      const symbol = updatedTrade.symbol?.toUpperCase();
      const quantity = parseInt(updatedTrade.quantity) || 1;
      
      const spec = getSpec(symbol);
      if (symbol && spec) {
        const riskTicks = safeTicks(stopLossPoints, spec.tickSize);
        const riskDollars = riskTicks * spec.tickValue * quantity;

        if (riskDollars > 0) {
          // R-Multiple = P&L / Risk (preserves the sign from P&L)
          rMultiple = (totalPnL / riskDollars).toFixed(2);
        }
      }
    }
    
    setTrade({
      ...updatedTrade,
      pnl: parseFloat(totalPnL.toFixed(2)),
      rMultiple: rMultiple
    });
  };

  // Wrapper for updateExit that also triggers recalculation
  const handleExitUpdate = (index, field, value) => {
    // First update the exit
    const newExits = (trade.exits || []).map((exit, i) => 
      i === index ? { ...exit, [field]: value } : exit
    );
    
    // Then recalculate with the new exits
    recalculateAll({ exits: newExits });
  };

  // Calculate ONLY R-Multiple when stop loss changes (don't touch P&L)
  const updateStopLossAndRMultiple = (updates) => {
    const updatedTrade = { ...trade, ...updates };
    
    // Get stop loss in points
    let stopLossPoints = 0;
    if (updatedTrade.useStopLossPoints && updatedTrade.stopLossPoints) {
      stopLossPoints = parseFloat(updatedTrade.stopLossPoints);
    } else if (updatedTrade.stopLoss && updatedTrade.entry) {
      stopLossPoints = Math.abs(parseFloat(updatedTrade.stopLoss) - parseFloat(updatedTrade.entry));
    }
    
    // Calculate R-Multiple using the STORED P&L (don't recalculate it)
    let rMultiple = '';
    const pnl = parseFloat(updatedTrade.pnl) || 0;
    
    if (stopLossPoints > 0 && pnl !== 0) {
      const symbol = updatedTrade.symbol?.toUpperCase();
      const quantity = parseInt(updatedTrade.quantity) || 1;
      
      const spec = getSpec(symbol);
      if (symbol && spec) {
        const riskTicks = safeTicks(stopLossPoints, spec.tickSize);
        const riskDollars = riskTicks * spec.tickValue * quantity;

        if (riskDollars > 0) {
          rMultiple = (pnl / riskDollars).toFixed(2);
        }
      }
    }
    
    setTrade({
      ...updatedTrade,
      rMultiple: rMultiple
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
      <div className={`rounded-2xl p-6 w-full max-w-[1800px] max-h-[90vh] overflow-y-auto transition-colors duration-300 ${
        darkMode ? 'bg-gray-950/95 backdrop-blur-2xl border border-white/10 shadow-2xl' : 'bg-white shadow-2xl'
      }`}>
        <div className="flex justify-between items-center mb-5">
          <h2 className={`text-2xl font-bold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {isEditing ? 'Edit Trade' : 'Add New Trade'}
          </h2>
          <button 
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors duration-300 ${
              darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Natural Language Input - Only show for new trades */}
        {!isEditing && (
          <div className={`mb-6 p-4 rounded-lg border transition-colors duration-300 ${
            darkMode ? 'bg-gray-800/30 border-gray-700/50' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <Mic className="text-purple-500" size={20} />
              <h3 className={`font-medium transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Quick Add with Natural Language
              </h3>
            </div>
            <input
              type="text"
              placeholder='Try: "Bought 2 NQ at 16,500, sold at 16,520, stop was 16,490"'
              value={voiceInput}
              onChange={(e) => setVoiceInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  const parsed = parseNaturalLanguageTrade(voiceInput);
                  setTrade({...trade, ...parsed});
                  setVoiceInput('');
                }
              }}
              className="w-full input-modern"
            />
            <p className={`text-xs mt-1 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Press Enter to parse and fill the form below
            </p>
          </div>
        )}

        {/* Two-column layout: Trade fields + Chart (v7.5.9 responsive).
            Below xl (1280px): stacks vertically — form on top, chart
            beneath (chart can stretch to full width then). At xl+:
            side-by-side with chart taking its natural width. Without this,
            narrow-viewport users got crushed form columns next to a chart
            that wouldn't shrink. */}
        <div className="flex flex-col xl:flex-row gap-4 xl:gap-6 mb-6">
          {/* Left column: Trade fields. min-w-0 lets it shrink below
              its content's intrinsic width on narrow screens (without
              this, long form rows would overflow the flex parent). */}
          <div className="flex-1 min-w-0">
            {/* Pre-Trade Coach (Phase 13) — shows historical context for the
                in-progress trade. Auto-collapses when no setup is selected;
                otherwise surfaces WR / count / avg R + winning recipe +
                time-of-day fit + missing recommended confluences. Sits
                above the form fields so the user sees it as they pick. */}
            <PreTradeCoachPanel
              trade={trade}
              allTrades={trades}
              allConfluences={allConfluences}
              setupDetails={setupDetails}
              getTradeNetPnL={getTradeNetPnL}
              getAccountById={getAccountById}
              onJumpToTrade={onJumpToTrade}
              darkMode={darkMode}
            />
            <div className="space-y-4">
              {/* Row 1: Date & Times (3 columns) */}
              <div className="grid grid-cols-3 gap-3">
            {/* Date */}
            <div>
              <label className={`block text-base font-medium mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>
                Trading Session
              </label>
              <input
                type="date"
                value={trade.date}
                onChange={(e) => setTrade({...trade, date: e.target.value})}
                className="w-full input-modern"
              />
            </div>
            {/* Entry Time */}
            <div>
              <label className={`block text-base font-medium mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>
                <Clock size={14} className="inline mr-1" />Entry Time
              </label>
              <input
                type="time"
                value={trade.entryTime || ''}
                onChange={(e) => setTrade({...trade, entryTime: e.target.value})}
                className="w-full input-modern"
              />
            </div>
            {/* Exit Time */}
            <div>
              <label className={`block text-base font-medium mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>
                <Clock size={14} className="inline mr-1" />Exit Time
              </label>
              <input
                type="time"
                value={trade.exitTime || ''}
                onChange={(e) => setTrade({...trade, exitTime: e.target.value})}
                className="w-full input-modern"
              />
            </div>
          </div>

          {/* Row 2: Account, Symbol, Direction, Setup (4 columns) */}
          <div className="grid grid-cols-[1fr_1fr_1fr_2fr] gap-3">
            {/* Account */}
            {accounts.length > 0 ? (
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <label className={`block text-base font-medium transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>Account</label>
                  <button
                    type="button"
                    onClick={() => setAccountHidden(!accountHidden)}
                    className={`p-0.5 rounded-sm transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
                    title={accountHidden ? 'Show account' : 'Hide account for screenshots'}
                  >
                    {accountHidden ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {accountHidden ? (
                  <div className={`w-full py-2 px-3 border rounded-lg text-sm ${
                    darkMode ? 'bg-gray-800/50 border-gray-700 text-gray-500' : 'bg-white border-gray-300 text-gray-400'
                  }`}>
                    ••••••••
                  </div>
                ) : (
                <GlassSelect
                  value={trade.accountId || ''}
                  onChange={(v) => setTrade({...trade, accountId: v})}
                  options={[
                    ...accounts.filter(a => a.status === 'Active').sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id)).map(account => ({
                      value: account.id,
                      label: account.name || account.id,
                    })),
                    ...accounts.filter(a => a.status !== 'Active').sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id)).map(account => ({
                      value: account.id,
                      label: account.name || account.id,
                      group: 'Inactive',
                    })),
                  ]}
                  placeholder="Select..."
                  darkMode={darkMode}
                  className="w-full"
                />
                )}
              </div>
            ) : (
              <div>
                <label className={`block text-base font-medium mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>Account</label>
                <input
                  type="text"
                  disabled
                  placeholder="No accounts"
                  className={`w-full py-2 px-3 border rounded-lg text-sm ${darkMode ? 'bg-gray-800/30 border-gray-700 text-gray-500' : 'bg-gray-100 border-gray-300 text-gray-400'}`}
                />
              </div>
            )}
            {/* Symbol */}
            <div>
              <label className={`block text-base font-medium mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>Symbol</label>
              <input
                type="text"
                placeholder="MNQ, ES..."
                value={trade.symbol || ''}
                onChange={(e) => recalculateAll({ symbol: e.target.value.toUpperCase() })}
                className="w-full input-modern"
              />
            </div>
            {/* Direction */}
            <div>
              <label className={`block text-base font-medium mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>Direction</label>
              <GlassSelect
                value={trade.direction || 'long'}
                onChange={(v) => {
                  if (isEditing) {
                    setTrade({...trade, direction: v});
                  } else {
                    recalculateAll({ direction: v });
                  }
                }}
                options={[
                  { value: 'long', label: 'Long' },
                  { value: 'short', label: 'Short' },
                ]}
                darkMode={darkMode}
                className="w-full"
              />
            </div>
            {/* Setup */}
            <div>
              <label className={`block text-base font-medium mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>Setup</label>
              <GlassSelect
                value={trade.setup || ''}
                onChange={(v) => setTrade({...trade, setup: v})}
                options={[...setups].sort((a, b) => a.localeCompare(b)).map(setup => ({ value: setup, label: setup }))}
                placeholder="Select Setup"
                darkMode={darkMode}
                className="w-full"
              />
            </div>
          </div>

          {/* Conviction (Phase 15 — Confidence Calibration). 1-5 stars set
              at entry time so the convictionCalibration analyzer can compute
              per-tier WR. Compact inline row — doesn't crowd the form. */}
          <div className={`flex items-center gap-3 mt-2`}>
            <span className={`text-xs uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Conviction
            </span>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map(n => {
                const active = String(trade.conviction || '') === String(n);
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setTrade({
                      ...trade,
                      // Click the active level to clear (un-rate) so the trade
                      // gets excluded from analysis instead of being forced to
                      // 1 just because the trader changed their mind.
                      conviction: active ? '' : n,
                    })}
                    title={`Conviction ${n}/5${active ? ' — click to clear' : ''}`}
                    className={`w-6 h-6 rounded-full text-xs font-semibold transition-colors ${
                      active
                        ? (darkMode ? 'bg-purple-500 text-white' : 'bg-purple-600 text-white')
                        : Number(trade.conviction) >= n
                          ? (darkMode ? 'bg-purple-500/40 text-purple-100' : 'bg-purple-200 text-purple-700')
                          : (darkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-200 text-gray-500 hover:bg-gray-300')
                    }`}
                  >
                    {n}
                  </button>
                );
              })}
              {trade.conviction && (
                <button
                  type="button"
                  onClick={() => setTrade({ ...trade, conviction: '' })}
                  className={`ml-1 text-[11px] underline ${darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-700'}`}
                  title="Clear conviction"
                >
                  clear
                </button>
              )}
            </div>
            <span className={`text-[11px] italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {!trade.conviction
                ? 'Optional — rate your conviction at entry; Coach tracks WR per tier'
                : Number(trade.conviction) <= 2 ? 'Low conviction — Coach will flag if these consistently lose'
                : Number(trade.conviction) === 3 ? 'Mid conviction'
                : 'High conviction — Coach will hold you to taking only these'}
            </span>
          </div>

          {/* Experiment (Phase 16 — A/B test grouping). Free-text label that
              groups variants of a deliberate test. Coach's experimentComparison
              analyzer compares trades sharing the same value to call out the
              winning variant. Datalist powers autocomplete from prior experiments.
              Compact inline row mirrors the Conviction layout above. */}
          <div className="flex items-center gap-3 mt-2">
            <span className={`text-xs uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Experiment
            </span>
            <div className="flex items-center gap-2 flex-1">
              <input
                type="text"
                list="experiment-suggestions"
                value={trade.experiment || ''}
                onChange={(e) => setTrade({ ...trade, experiment: e.target.value })}
                placeholder="e.g. SL Test 5/15 — leave blank if not testing"
                className={`flex-1 max-w-md py-1.5 px-2.5 text-sm rounded-md border transition-colors ${
                  darkMode
                    ? 'bg-gray-800/50 border-gray-700 text-white focus:border-purple-500/60 focus:ring-1 focus:ring-purple-500/40'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-purple-500 focus:ring-1 focus:ring-purple-300/50'
                } focus:outline-none`}
              />
              {/* Datalist drives the autocomplete dropdown. Sourced from prior
                  trades' experiment values — recent and unique, oldest pruned.
                  Native datalist avoids needing a custom popover component. */}
              <datalist id="experiment-suggestions">
                {Array.from(
                  new Set(
                    (Array.isArray(trades) ? trades : [])
                      .map(t => (t?.experiment || '').trim())
                      .filter(Boolean)
                  )
                ).slice(-10).reverse().map(name => (
                  <option key={name} value={name} />
                ))}
              </datalist>
              {trade.experiment && (
                <button
                  type="button"
                  onClick={() => setTrade({ ...trade, experiment: '' })}
                  className={`text-[11px] underline ${darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-700'}`}
                  title="Clear experiment"
                >
                  clear
                </button>
              )}
            </div>
            <span className={`text-[11px] italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {trade.experiment
                ? `Coach will compare variants in "${trade.experiment}" by R-multiple + WR`
                : 'Group A/B variants (e.g. different SLs) so Coach can compare them'}
            </span>
          </div>

          {/* Row 3: Quantity, Entry Price, Stop Loss, Wick Over, ATM Strategy.
              Responsive breakpoints (v7.5.9):
                base (< sm):    2 columns — labels finally readable, two
                                fields per row, ATM dropdown wraps to full
                                width via col-span-2 on its own row.
                sm (640px+):    3 columns — fits Quantity / Entry / Stop on
                                one row, Wick / ATM on next.
                lg (1024px+):   full 5-column layout as before.
              Without this, narrow viewports squished labels into 2-line wraps
              ("Stop Loss" → "Stop / Loss") and truncated the entry price input. */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-[1fr_1fr_1fr_1fr_2fr] gap-3">
            {/* Quantity */}
            <div>
              <label className={`block text-base font-medium mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>Quantity</label>
              <input
                type="number"
                placeholder="1"
                value={trade.quantity || ''}
                onChange={(e) => recalculateAll({ quantity: e.target.value })}
                className="w-full input-modern"
              />
            </div>
            {/* Entry Price */}
            <div>
              <label className={`block text-base font-medium mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>Entry Price</label>
              <input
                type="number"
                step="0.25"
                placeholder="0.00"
                value={trade.entry || ''}
                onChange={(e) => recalculateAll({ entry: e.target.value })}
                className="w-full input-modern"
              />
            </div>
            {/* Stop Loss */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={`text-sm font-medium transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Stop Loss
                </label>
                <button
                  type="button"
                  onClick={() => setTrade({...trade, useStopLossPoints: !trade.useStopLossPoints})}
                  className={`text-xs px-1.5 py-0.5 rounded transition-colors duration-300 ${
                    trade.useStopLossPoints
                      ? 'bg-blue-500 text-white'
                      : darkMode
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {trade.useStopLossPoints ? 'Points' : 'Level'}
                </button>
              </div>
              <input
                type="number"
                step="0.25"
                placeholder={trade.useStopLossPoints ? "Points" : "Level"}
                value={trade.useStopLossPoints ? (trade.stopLossPoints || '') : (trade.stopLoss || '')}
                onChange={(e) => trade.useStopLossPoints 
                  ? updateStopLossAndRMultiple({ stopLossPoints: e.target.value })
                  : updateStopLossAndRMultiple({ stopLoss: e.target.value })
                }
                className="w-full input-modern"
              />
            </div>
            {/* Wick Over */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={`text-sm font-medium transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Wick Over
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const newVal = trade.useWickOverTicks === false;
                    setTrade({...trade, useWickOverTicks: newVal});
                    if (onWickOverUnitChange) onWickOverUnitChange(newVal);
                  }}
                  className={`text-xs px-1.5 py-0.5 rounded transition-colors duration-300 ${
                    trade.useWickOverTicks !== false
                      ? 'bg-blue-500 text-white'
                      : darkMode
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {trade.useWickOverTicks !== false ? 'Ticks' : 'Points'}
                </button>
              </div>
              <input
                type="number"
                step={trade.useWickOverTicks !== false ? "1" : "0.25"}
                min="0"
                placeholder=""
                value={trade.wickOver || ''}
                onChange={(e) => setTrade({...trade, wickOver: e.target.value})}
                className="w-full input-modern"
              />
            </div>
            {/* ATM Strategy — spans both columns at base width so it doesn't
                sit alone in row 3 col 1 (with col 2 empty next to it). At
                sm+ it returns to single-cell behavior since the grid widens. */}
            <div className="relative col-span-2 sm:col-span-1">
              <label className={`block text-base font-medium mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>
                ATM Strategy
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Select or type..."
                  value={trade.atmStrategy || ''}
                  onChange={(e) => handleAtmChange(e.target.value)}
                  onFocus={() => setShowAtmDropdown(true)}
                  className="w-full input-modern pr-8"
                />
                {existingAtmStrategies.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAtmDropdown(!showAtmDropdown)}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-600/50 transition-colors ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    <ChevronDown size={16} className={`transition-transform ${showAtmDropdown ? 'rotate-180' : ''}`} />
                  </button>
                )}
                {showAtmDropdown && existingAtmStrategies.length > 0 && (
                  <div className={`absolute z-50 min-w-full w-max mt-1 max-h-48 overflow-y-auto border rounded-lg shadow-xl ${
                    darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'
                  }`}>
                    {existingAtmStrategies
                      .filter(atm => !trade.atmStrategy || atm.toLowerCase().includes(trade.atmStrategy.toLowerCase()))
                      .map((atm, idx) => {
                        // v7.6.0 — annotate archived templates so the trader
                        // knows the ATM is no longer in NinjaTrader. The
                        // dropdown still lets you SELECT an archived template
                        // (a trade might genuinely reference an old one), but
                        // the visual cue prevents confusion when picking
                        // ATMs for new trades.
                        const archived = isAtmArchived(ntTemplateVersions, atm);
                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => handleAtmChange(atm)}
                            className={`w-full text-left px-3 py-2 text-sm whitespace-nowrap transition-colors ${
                              darkMode
                                ? 'hover:bg-gray-600 text-gray-200'
                                : 'hover:bg-gray-100 text-gray-700'
                            } ${trade.atmStrategy === atm ? (darkMode ? 'bg-gray-600' : 'bg-gray-100') : ''}`}
                            title={archived ? `${atm} (archived — no longer in NinjaTrader)` : atm}
                          >
                            <span>{atm}</span>
                            {archived && (
                              <span className={`ml-2 text-[10px] italic ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                                (archived)
                              </span>
                            )}
                          </button>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ATM Suggestion (spans full width when visible) */}
          {atmSuggestion && (
            <div className={`p-2 rounded-lg flex items-center justify-between text-sm ${
              darkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'
            }`}>
              <span className={darkMode ? 'text-blue-200' : 'text-blue-700'}>
                ATM matches Setup: <strong>{atmSuggestion.setupName}</strong> — Apply?
              </span>
              <div className="flex gap-1 ml-2">
                <button
                  type="button"
                  onClick={applySuggestedSetup}
                  className="px-2 py-1 bg-blue-500 text-white rounded-sm hover:bg-blue-600 transition-colors text-xs"
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={dismissSuggestion}
                  className={`px-2 py-1 rounded transition-colors text-xs ${
                    darkMode 
                      ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  No
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Exits Section - Compact */}
        <div className={`mb-4 p-3 rounded-lg border transition-colors duration-300 ${
          darkMode ? 'bg-gray-800/30 border-gray-700/50' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <label className={`text-sm font-medium transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Exits / Trims
            </label>
            <div className="flex items-center gap-2">
              {trade.quantity && (
                <span className={`text-xs px-2 py-0.5 rounded transition-colors duration-300 ${
                  getRemainingContracts() === 0 
                    ? 'bg-green-500/20 text-green-500'
                    : getRemainingContracts() < 0
                      ? 'bg-red-500/20 text-red-500'
                      : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                }`}>
                  {getRemainingContracts() === 0 
                    ? '✓ All assigned'
                    : getRemainingContracts() < 0
                      ? `${Math.abs(getRemainingContracts())} over`
                      : `${getRemainingContracts()} left`
                  }
                </span>
              )}
              <button
                type="button"
                onClick={addExit}
                className="text-xs px-2 py-1 bg-blue-500 text-white rounded-sm hover:bg-blue-600 transition-colors duration-300 flex items-center gap-1"
              >
                <Plus size={12} />
                Add
              </button>
            </div>
          </div>
          
          <div className="space-y-1.5">
            {(trade.exits || []).map((exit, index) => {
              const pnl = calculateExitPnL(exit);
              const rMultiple = calculateExitRMultiple(exit);
              const hasPnL = exit.quantity && exit.price && trade.entry;
              
              return (
                <div key={index} className={`flex items-center gap-2 p-1.5 rounded border transition-colors duration-300 ${
                  darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-gray-300'
                }`}>
                  {/* Qty */}
                  <input
                    type="number"
                    min="1"
                    placeholder="Qty"
                    value={exit.quantity || ''}
                    onChange={(e) => handleExitUpdate(index, 'quantity', e.target.value)}
                    className="w-16 input-modern text-center"
                  />
                  <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>@</span>
                  {/* Price */}
                  <input
                    type="number"
                    step="0.25"
                    placeholder="Exit Price"
                    value={exit.price || ''}
                    onChange={(e) => handleExitUpdate(index, 'price', e.target.value)}
                    className="w-24 input-modern"
                  />
                  {/* Time */}
                  <input
                    type="time"
                    value={exit.time || ''}
                    onChange={(e) => handleExitUpdate(index, 'time', e.target.value)}
                    className="w-32 input-modern"
                  />
                  {/* P&L and R inline */}
                  <div className="flex-1 flex items-center justify-end gap-2 text-xs">
                    {hasPnL && pnl !== 0 && (
                      <>
                        <span className={`font-medium ${pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
                        </span>
                        {rMultiple !== null && (
                          <span className={`${rMultiple >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            ({rMultiple >= 0 ? '+' : ''}{rMultiple.toFixed(2)}R)
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {/* Remove button */}
                  {(trade.exits || []).length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeExit(index)}
                      className={`p-1 rounded hover:bg-red-500 hover:text-white transition-colors duration-300 ${
                        darkMode ? 'text-gray-500' : 'text-gray-400'
                      }`}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          </div>

          {/* P&L and R-Multiple */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`text-base font-medium transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                P&L {isEditing && trade.ntProfit ? '(from import)' : '(Auto-calculated)'}
              </label>
              {trade.entry && (trade.exits || []).some(e => e.quantity && e.price) && (
                <button
                  type="button"
                  onClick={() => recalculateAll()}
                  className="text-xs px-2 py-1 bg-blue-500 text-white rounded-sm hover:bg-blue-600 transition-colors"
                >
                  Recalculate
                </button>
              )}
            </div>
            <input
              type="number"
              step="0.01"
              value={trade.pnl || ''}
              onChange={(e) => setTrade({...trade, pnl: e.target.value === '' ? '' : parseFloat(e.target.value) || 0})}
              className={`w-full input-modern ${
                trade.pnl
                  ? (parseFloat(trade.pnl) >= 0 ? 'text-green-500' : 'text-red-500')
                  : ''
              }`}
            />
            {/* Commission Display */}
            {(() => {
              const selectedAccount = accounts.find(a => a.id === trade.accountId);
              const quantity = parseInt(trade.quantity) || 0;
              const symbol = trade.symbol?.toUpperCase();
              
              // Use centralized commission calculation
              const commission = getTradeCommission(trade, selectedAccount);
              const pnl = parseFloat(trade.pnl) || 0;
              const netPnL = pnl - commission;
              
              // Get rate info for display purposes
              let commissionPerSide = selectedAccount?.commissionPerSide || 0;
              let isSymbolRate = false;
              let broker = selectedAccount?.broker;
              let dataProvider = selectedAccount?.dataProvider;
              let isImported = false;
              
              // Check if using imported commission
              if (trade.importedCommission && trade.importedCommission > 0) {
                isImported = true;
              } else {
                // Get symbol-specific rate for display
                const symbolRate = getCommissionRate(broker, symbol, dataProvider);
                if (symbolRate !== null) {
                  commissionPerSide = symbolRate;
                  isSymbolRate = true;
                }
              }
              
              if (commission > 0 && quantity > 0) {
                return (
                  <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className={`flex justify-between items-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <span>
                        {isImported ? (
                          <>Commission (imported):</>
                        ) : (
                          <>
                            Commission ({quantity} × 2 × ${commissionPerSide.toFixed(2)}
                            {isSymbolRate && <span className="text-cyan-400 ml-1">• {symbol}</span>}):
                          </>
                        )}
                      </span>
                      <span className="text-red-400 font-medium">-${fmtUSD(commission)}</span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Net P&L:</span>
                      <span className={`text-2xl font-bold ${netPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {netPnL >= 0 ? '+' : '-'}${fmtUSD(netPnL)}
                      </span>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`text-base font-medium transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                R Multiple
              </label>
              {trade.entry && (trade.stopLoss || trade.stopLossPoints) && trade.pnl && (
                <button
                  type="button"
                  onClick={() => recalculateAll()}
                  className="text-xs px-2 py-1 bg-blue-500 text-white rounded-sm hover:bg-blue-600 transition-colors"
                >
                  Recalculate
                </button>
              )}
            </div>
            <input
              type="number"
              step="0.1"
              placeholder="0.0"
              value={trade.rMultiple || ''}
              onChange={(e) => setTrade({...trade, rMultiple: e.target.value})}
              className={`w-full input-modern ${
                trade.rMultiple && parseFloat(trade.rMultiple) !== 0
                  ? (parseFloat(trade.rMultiple) > 0 ? 'text-green-500' : 'text-red-500')
                  : ''
              }`}
            />
          </div>
          </div>
          </div>

          {/* Right column: Chart (with markers) OR legacy Screenshot upload.
              v7.5.9: width is now responsive — full available width on narrow
              (stacked layout), fixed 744px at xl+ where it sits side-by-side
              with the form. `shrink-0` only applies at xl+ so the chart can't
              be squeezed when there IS room next to the form. */}
          <div className="w-full xl:w-[744px] xl:shrink-0">
            <div className="flex items-center justify-between mb-2">
              <label className={`block text-base font-medium transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>
                {rightColView === 'chart' ? (
                  <><BarChart3 size={14} className="inline mr-1" /> Trade Chart</>
                ) : (
                  <><Image size={14} className="inline mr-1" /> Chart Screenshot</>
                )}
              </label>
              {/* Chart / Screenshot toggle */}
              <div className={`inline-flex rounded-lg p-0.5 ${darkMode ? 'bg-gray-800/60' : 'bg-gray-100'}`}>
                <button
                  type="button"
                  onClick={() => setRightColView('chart')}
                  className={`text-xs px-2.5 py-1 rounded font-medium transition-colors ${
                    rightColView === 'chart'
                      ? (darkMode ? 'bg-purple-500/30 text-purple-100' : 'bg-white text-purple-700 shadow-sm')
                      : (darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
                  }`}
                >
                  <BarChart3 size={11} className="inline mr-1" />
                  Chart
                </button>
                <button
                  type="button"
                  onClick={() => setRightColView('screenshot')}
                  className={`text-xs px-2.5 py-1 rounded font-medium transition-colors ${
                    rightColView === 'screenshot'
                      ? (darkMode ? 'bg-purple-500/30 text-purple-100' : 'bg-white text-purple-700 shadow-sm')
                      : (darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
                  }`}
                >
                  <Image size={11} className="inline mr-1" />
                  Screenshot
                </button>
              </div>
            </div>

            {rightColView === 'chart' ? (
              // min-h instead of fixed h: TradeChart is ~chartHeight + 80px
              // chrome (header + replay bar) and the column also stacks the
              // source badge, MAE/MFE row, and Coach panel underneath.
              // Constraining to a hard height squishes the chart's bottom
              // edge — that's where the time axis lives, which is why time
              // labels would intermittently vanish. The min-h tracks
              // chartHeight + chrome so the container always has enough room.
              <div className="flex flex-col" style={{ minHeight: chartHeight + 80 }}>
                {chartBars.length > 0 ? (
                  <>
                    <TradeChart
                      bars={chartBars}
                      entries={chartFills.entries}
                      exits={chartFills.exits}
                      direction={trade?.direction || 'long'}
                      stopLoss={chartStopLoss}
                      takeProfit={chartTakeProfit}
                      symbol={trade?.symbol}
                      tradeDate={trade?.date}
                      timeframe={chartTimeframe}
                      onTimeframeChange={setChartTimeframe}
                      onRefresh={handleManualChartRefresh}
                      isRefreshing={chartRefreshing}
                      enableReplay={true}
                      darkMode={darkMode}
                      height={chartHeight}
                      barAnalysis={barAnalysis}
                      emaPeriods={chartSettings?.emaPeriods || undefined}
                      emaColors={chartSettings?.emaColors || undefined}
                      defaultZoomBars={chartSettings?.defaultZoomBars || 72}
                    />
                    {/* Source badge — shows which bar source served the data
                        (Local CSV, NinjaTrader, sample fallback, etc.). Helps
                        the user understand whether they're looking at real
                        price action or scaffolding data. */}
                    {chartBarsMeta.source && (() => {
                      // Last bar's age relative to wall-clock. For today's
                      // session this exposes how stale JournifiBars (or
                      // whichever live source) is — invaluable when the chart
                      // looks "frozen" because the AddOn isn't writing live.
                      // Past-day trades skip this since "stale" doesn't apply.
                      let isLiveDay = false;
                      try { isLiveDay = trade?.date === getCurrentTradingDay(); } catch { /* */ }
                      const firstBar = chartBars && chartBars.length > 0 ? chartBars[0] : null;
                      const lastBar = chartBars && chartBars.length > 0 ? chartBars[chartBars.length - 1] : null;
                      const fmtHHMM = (d) => d
                        ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
                        : null;
                      const firstBarTime = firstBar?.time ? new Date(firstBar.time * 1000) : null;
                      const lastBarTime  = lastBar?.time  ? new Date(lastBar.time * 1000)  : null;
                      const firstBarLabel = fmtHHMM(firstBarTime);
                      const lastBarLabel  = fmtHHMM(lastBarTime);
                      const ageMin = lastBarTime ? Math.floor((Date.now() - lastBarTime.getTime()) / 60000) : null;
                      // Threshold for "this looks stale" warning — anything
                      // more than 10 minutes behind during a live session
                      // probably means JournifiBars stopped writing.
                      const looksStale = isLiveDay && ageMin != null && ageMin > 10;
                      const barCount = chartBars?.length || 0;
                      return (
                        <div className={`mt-1 text-[10px] flex items-center gap-1.5 flex-wrap ${
                          chartBarsMeta.fallback || looksStale
                            ? (darkMode ? 'text-amber-300/80' : 'text-amber-700/80')
                            : (darkMode ? 'text-gray-500' : 'text-gray-400')
                        }`}>
                          <span>Source: {chartBarsMeta.source}</span>
                          {chartBarsMeta.fromCache && <span className="opacity-70">(cached)</span>}
                          {firstBarLabel && lastBarLabel && (
                            <span
                              className="opacity-80"
                              title={
                                `${barCount} bars loaded. ` +
                                `Range: ${firstBarLabel} → ${lastBarLabel}. ` +
                                `If the first bar is later than the start of your trading session, ` +
                                `JournifiBars may not have been writing during that period — verify in NT8 Tools → Output Window.`
                              }
                            >
                              · {firstBarLabel} → {lastBarLabel}
                              {ageMin != null ? ` (${ageMin}m ago)` : ''}
                              <span className={`ml-1 ${darkMode ? 'text-gray-600' : 'text-gray-500'}`}>· {barCount} bars</span>
                            </span>
                          )}
                          {looksStale && (
                            <span className="opacity-100 font-medium">
                              — bars look stale; check JournifiBars in NT8 (Tools → Output Window)
                            </span>
                          )}
                          {chartBarsMeta.fallback && <span className="opacity-90">— drop a CSV in {`{dataDir}`}/bars/ to use real data</span>}
                        </div>
                      );
                    })()}

                    {/* Bar-derived MAE/MFE — auto-computed from the loaded bars
                        and persisted on the trade so Coach analyzers can read it.
                        Shown only when real bars produced a result. */}
                    {barAnalysis && !chartBarsMeta.fallback && (
                      <div className={`mt-1.5 flex items-center gap-3 text-[11px] ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        <span title={`${barAnalysis.maePoints.toFixed(2)} pts adverse · ${barAnalysis.barsConsidered} bars`}>
                          <span className={`uppercase tracking-wider mr-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>MAE</span>
                          <span className="text-red-400 font-mono">
                            {barAnalysis.maeDollars != null ? `−$${Math.round(barAnalysis.maeDollars).toLocaleString()}` : `${barAnalysis.maePoints.toFixed(2)} pts`}
                          </span>
                          {barAnalysis.maeR != null && (
                            <span className="ml-1 text-red-400/70 font-mono">({barAnalysis.maeR.toFixed(2)}R)</span>
                          )}
                        </span>
                        <span className={darkMode ? 'text-gray-700' : 'text-gray-300'}>·</span>
                        <span title={`${barAnalysis.mfePoints.toFixed(2)} pts favorable`}>
                          <span className={`uppercase tracking-wider mr-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>MFE</span>
                          <span className="text-green-400 font-mono">
                            {barAnalysis.mfeDollars != null ? `+$${Math.round(barAnalysis.mfeDollars).toLocaleString()}` : `${barAnalysis.mfePoints.toFixed(2)} pts`}
                          </span>
                          {barAnalysis.mfeR != null && (
                            <span className="ml-1 text-green-400/70 font-mono">({barAnalysis.mfeR.toFixed(2)}R)</span>
                          )}
                        </span>
                        <span className={`opacity-60 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>· auto from bars</span>
                      </div>
                    )}

                    {/* Per-trade Coach narrative — appears when the analyzer
                        produces an opinion on this specific trade (A-grade,
                        wicked-out, round-trip, fought-HTF, etc.). Renders
                        nothing when the trade is mid-tier or has no
                        barAnalysis yet — keeps the modal calm. */}
                    {tradeObservations.length > 0 && (
                      <div className="mt-3">
                        <CoachObservationsPanel
                          observations={tradeObservations}
                          title="AI Coach"
                          compact
                          darkMode={darkMode}
                          onAction={onCoachAction}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className={`h-full flex items-center justify-center rounded-xl border-2 border-dashed ${
                    darkMode ? 'border-gray-700 bg-gray-800/30 text-gray-400' : 'border-gray-300 bg-gray-50 text-gray-500'
                  }`}>
                    <div className="text-center text-sm px-6">
                      <BarChart3 size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="font-medium mb-1">No chart data yet</p>
                      <p className="text-xs opacity-80">
                        Set a symbol and date to see the chart with entry/exit markers.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
            <div
              className={`border-2 border-dashed rounded-xl h-[500px] flex flex-col items-center justify-center transition-colors ${
                darkMode ? 'border-gray-700 bg-gray-800/30 hover:border-gray-600' : 'border-gray-300 bg-gray-50 hover:border-gray-400'
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const file = e.dataTransfer.files?.[0];
                if (file && file.type.startsWith('image/')) {
                  const reader = new FileReader();
                  reader.onload = (event) => {
                    setTrade({...trade, chartScreenshot: event.target.result});
                  };
                  reader.readAsDataURL(file);
                }
              }}
              onPaste={(e) => {
                const items = e.clipboardData?.items;
                if (items) {
                  for (const item of items) {
                    if (item.type.startsWith('image/')) {
                      const file = item.getAsFile();
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setTrade({...trade, chartScreenshot: event.target.result});
                        };
                        reader.readAsDataURL(file);
                      }
                      break;
                    }
                  }
                }
              }}
              tabIndex={0}
            >
              {trade.chartScreenshot ? (
                <div className="relative w-full h-full p-2">
                  <img 
                    src={trade.chartScreenshot} 
                    alt="Trade chart" 
                    className="w-full h-full object-contain rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setTrade({...trade, chartScreenshot: ''})}
                    className="absolute top-4 right-4 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors shadow-lg"
                    title="Remove screenshot"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer flex flex-col items-center gap-3 p-6 w-full h-full justify-center">
                  <div className={`p-4 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <Upload size={32} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                  </div>
                  <div className="text-center">
                    <p className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Drop chart image here
                    </p>
                    <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      or click to browse
                    </p>
                    <p className={`text-xs mt-2 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                      Tip: Ctrl+V to paste from clipboard
                    </p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setTrade({...trade, chartScreenshot: event.target.result});
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                </label>
              )}
            </div>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label className={`block text-base font-medium mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>Notes</label>
          <RichTextEditor
            value={trade.notes || ''}
            onChange={(value) => setTrade({...trade, notes: value})}
            placeholder="Trade notes, market conditions, lessons learned..."
            darkMode={darkMode}
            minHeight="80px"
            templateType="tradeNotes"
          />
        </div>

        {/* Quick Confluence Matcher */}
        <div className="mb-6">
          <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Quick Add Confluences
          </label>
          <div className="flex gap-2">
            <input
              ref={confluenceInputRef}
              type="text"
              value={confluenceMatcherInput}
              onChange={(e) => setConfluenceMatcherInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleConfluenceMatch(); } }}
              placeholder='e.g. "ema, rsi, against choppy, not low volume"'
              className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors duration-300 ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500'
                  : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'
              }`}
            />
            <button
              type="button"
              onClick={toggleVoiceInput}
              title={isListening ? 'Stop listening' : 'Voice input'}
              className={`px-2.5 py-2 rounded-lg text-sm transition-[background-color,border-color,box-shadow,color] duration-150 ${
                isListening
                  ? 'bg-red-500 text-white animate-pulse'
                  : darkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
              }`}
            >
              <Mic size={16} />
            </button>
            <button
              type="button"
              onClick={handleConfluenceMatch}
              disabled={!confluenceMatcherInput.trim()}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-300 ${
                darkMode
                  ? 'bg-purple-600 text-white hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500'
                  : 'bg-purple-500 text-white hover:bg-purple-600 disabled:bg-gray-200 disabled:text-gray-400'
              }`}
            >
              Match
            </button>
          </div>
          {/* Live preview: shows matches as you type (after each comma) */}
          {matchPreview.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5 items-center">
              {matchPreview.map((p, i) => (
                <span
                  key={i}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    p.name
                      ? p.isAgainst
                        ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                        : 'bg-green-500/20 text-green-300 border border-green-500/30'
                      : 'bg-red-500/20 text-red-300 border border-red-500/30'
                  }`}
                  title={p.name ? `${p.term} → ${p.name} (score: ${p.score})` : `No match for "${p.term}"`}
                >
                  {p.name ? (
                    <>{p.isAgainst && <span className="opacity-60">against</span>} {p.name}</>
                  ) : (
                    <>{p.term} <span className="opacity-60">?</span></>
                  )}
                </span>
              ))}
            </div>
          )}
          {/* Post-commit feedback */}
          {confluenceMatcherFeedback && (
            <div className={`mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {confluenceMatcherFeedback.matched.length > 0 && (
                <span className="text-green-400">
                  Matched: {confluenceMatcherFeedback.matched.map(m =>
                    `${m.name}${m.isAgainst ? ' (against)' : ''}`
                  ).join(', ')}
                </span>
              )}
              {confluenceMatcherFeedback.matched.length > 0 && confluenceMatcherFeedback.notFound.length > 0 && (
                <span> | </span>
              )}
              {confluenceMatcherFeedback.notFound.length > 0 && (
                <span className="text-yellow-400">
                  Not found: {confluenceMatcherFeedback.notFound.join(', ')}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Event Tags */}
        <div className="mb-6">
          <label className={`block text-lg font-semibold mb-1 text-center transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Event Tags</label>
          <div className="flex flex-wrap justify-center gap-2">
            {eventTags.map(tag => (
              <button
                key={tag}
                type="button"
                onClick={() => handleTagToggle(tag)}
                className={`px-3 py-1 rounded-full text-sm transition-colors duration-300 ${
                  (trade.tags || []).includes(tag)
                    ? 'bg-blue-500 text-white'
                    : darkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Mistakes */}
        <div className="mb-6">
          <label className={`block text-lg font-semibold mb-1 text-center transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Mistakes</label>
          <div className="flex flex-wrap justify-center gap-2">
            {mistakes.map(mistake => (
              <button
                key={mistake}
                type="button"
                onClick={() => handleMistakeToggle(mistake)}
                className={`px-3 py-1 rounded-full text-sm transition-colors duration-300 ${
                  (trade.mistakes || []).includes(mistake)
                    ? 'bg-red-500 text-white'
                    : darkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {mistake}
              </button>
            ))}
          </div>
        </div>

        {/* Confluences - Market Conditions */}
        {confluences.length > 0 && (
          <div className="mb-6">
            <label className={`block text-lg font-semibold mb-1 text-center transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Confluences / Market Conditions
            </label>
            
            {/* Show recommended confluences hint if setup has them */}
            {(() => {
              const selectedSetup = setupsWithDetails.find(s => s.name === trade.setup);
              const recommendedIds = selectedSetup?.recommendedConfluences || [];
              if (recommendedIds.length > 0 && trade.setup && trade.setup !== 'No Setup') {
                const selectedCount = recommendedIds.filter(id => (trade.confluences || []).includes(id)).length;
                return (
                  <div className={`mb-2 p-2 rounded-lg text-xs ${
                    darkMode ? 'bg-purple-900/30 border border-purple-500/30' : 'bg-purple-50 border border-purple-200'
                  }`}>
                    <span className={darkMode ? 'text-purple-300' : 'text-purple-700'}>
                      <span className="font-medium">{trade.setup}</span> has {recommendedIds.length} recommended confluences 
                      {selectedCount > 0 && <span className="font-bold"> ({selectedCount} selected)</span>}
                    </span>
                  </div>
                );
              }
              return null;
            })()}
            
            {/* Categories rendered as a searchable, smart-auto-expanding
                accordion. Replaces a wall of ~100 chips across ~12 categories.
                Categories with selections auto-expand on open; empty ones
                stay collapsed. Search filter at the top filters chips live
                across all categories. See ConfluenceCategoriesAccordion. */}
            <ConfluenceCategoriesAccordion
              confluences={confluences}
              selectedIds={trade.confluences || []}
              oppositeSelectedIds={trade.counterConfluences || []}
              recommendedIds={setupsWithDetails.find(s => s.name === trade.setup)?.recommendedConfluences || []}
              onToggle={handleConfluenceToggle}
              darkMode={darkMode}
              selectedSetupName={trade.setup}
              variant="for"
            />

            {/* Against Trade (Counter-Confluences) — Collapsible */}
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowCounterConfluences(!showCounterConfluences)}
                className={`flex items-center gap-2 text-sm font-medium transition-colors duration-300 ${
                  darkMode ? 'text-orange-400 hover:text-orange-300' : 'text-orange-600 hover:text-orange-700'
                }`}
              >
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${showCounterConfluences ? 'rotate-0' : '-rotate-90'}`}
                />
                Against Trade
                {(trade.counterConfluences || []).length > 0 && (
                  <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                    darkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-800'
                  }`}>
                    {(trade.counterConfluences || []).length}
                  </span>
                )}
              </button>

              {showCounterConfluences && (
                <div className="mt-2 pl-1">
                  <ConfluenceCategoriesAccordion
                    confluences={confluences}
                    selectedIds={trade.counterConfluences || []}
                    oppositeSelectedIds={trade.confluences || []}
                    onToggle={handleCounterConfluenceToggle}
                    darkMode={darkMode}
                    variant="counter"
                  />
                </div>
              )}
            </div>

            {/* Bulk Copy Confluences Button */}
            {isEditing && onBulkCopyConfluences && ((trade.confluences || []).length > 0 || (trade.counterConfluences || []).length > 0) && (
              <div className="mt-3">
                {!showBulkCopy ? (
                  <button
                    type="button"
                    onClick={() => { setShowBulkCopy(true); setBulkCopySelectedTrades([]); }}
                    className={`flex items-center gap-1 text-xs transition-colors ${
                      darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-600'
                    }`}
                  >
                    <Copy size={14} />
                    Copy Confluences to Other Trades...
                  </button>
                ) : (
                  <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Copy Confluences to Other Trades
                      </span>
                      <button type="button" onClick={() => setShowBulkCopy(false)} className={`p-1 rounded-sm ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
                        <X size={16} />
                      </button>
                    </div>

                    {/* Selected confluences preview — implied confluences are
                        EXCLUDED so the preview matches what actually gets
                        copied (the apply handler filters them out before
                        propagating). Without this, the preview chips
                        promised more than the operation delivered and
                        traders thought their setup-implied chips would
                        carry over to the target trade.
                        v7.6.x — Phase 18 guard. */}
                    {(() => {
                      const srcImplied = Array.isArray(setupDetails?.[trade.setup]?.impliedConfluences)
                        ? setupDetails[trade.setup].impliedConfluences
                        : [];
                      const previewConf    = (trade.confluences || []).filter(c => !srcImplied.includes(c));
                      const previewCounter = (trade.counterConfluences || []);
                      const skipped        = (trade.confluences || []).length - previewConf.length;
                      return (
                        <>
                          <div className="flex flex-wrap gap-1 mb-1">
                            {previewConf.map(cId => {
                              const c = confluences.find(cf => cf.id === cId);
                              return c ? (
                                <span key={cId} className="px-2 py-0.5 text-xs bg-purple-500 text-white rounded-full">{c.name}</span>
                              ) : null;
                            })}
                            {previewCounter.map(cId => {
                              const c = confluences.find(cf => cf.id === cId);
                              return c ? (
                                <span key={`cc-${cId}`} className="px-2 py-0.5 text-xs bg-orange-500 text-white rounded-full">✗ {c.name}</span>
                              ) : null;
                            })}
                          </div>
                          {skipped > 0 && (
                            <p className={`text-[10px] mb-2 italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              Skipping {skipped} implied confluence{skipped > 1 ? 's' : ''} from &ldquo;{trade.setup}&rdquo; — those auto-apply per setup, not per trade.
                            </p>
                          )}
                        </>
                      );
                    })()}

                    {/* Mode toggle */}
                    <div className="flex gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => setBulkCopyMode('add')}
                        className={`flex-1 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                          bulkCopyMode === 'add'
                            ? 'bg-blue-500 text-white border-blue-500'
                            : darkMode ? 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        Add to existing
                      </button>
                      <button
                        type="button"
                        onClick={() => setBulkCopyMode('replace')}
                        className={`flex-1 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                          bulkCopyMode === 'replace'
                            ? 'bg-orange-500 text-white border-orange-500'
                            : darkMode ? 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        Replace existing
                      </button>
                    </div>

                    {/* Filters */}
                    {(() => {
                      const otherTrades = trades.filter(t => t.id !== trade.id);
                      const symbols = [...new Set(otherTrades.map(t => t.symbol))].sort();

                      // Date filter logic (trading day = 5:15pm ET rollover).
                      // String-based arithmetic via timeService — no UTC drift.
                      const todayStr = getCurrentTradingDay();
                      const weekAgoStr = addDaysToDateStr(todayStr, -7);
                      const monthAgoStr = addDaysToDateStr(todayStr, -30);

                      // Session-aware sort (descending) via timeService: evening trades
                      // (entryTime >= 18:00) actually executed on the prior calendar day, so
                      // they correctly precede next-morning trades on the same trading day.
                      const filteredBulkTrades = otherTrades.filter(t => {
                        if (bulkCopyDateFilter === 'today' && t.date !== todayStr) return false;
                        if (bulkCopyDateFilter === 'week' && t.date < weekAgoStr) return false;
                        if (bulkCopyDateFilter === 'month' && t.date < monthAgoStr) return false;
                        if (bulkCopySymbolFilter !== 'all' && t.symbol !== bulkCopySymbolFilter) return false;
                        return true;
                      }).sort((a, b) => getExecutionInstantMs(b.date, b.entryTime) - getExecutionInstantMs(a.date, a.entryTime));

                      return (
                        <>
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Filter size={12} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                            {/* Date filter chips */}
                            {[
                              { key: 'today', label: 'Today' },
                              { key: 'week', label: 'This Week' },
                              { key: 'month', label: 'This Month' },
                              { key: 'all', label: 'All' }
                            ].map(f => (
                              <button
                                key={f.key}
                                type="button"
                                onClick={() => { setBulkCopyDateFilter(f.key); setBulkCopySelectedTrades([]); }}
                                className={`px-2 py-0.5 text-[10px] rounded-full border transition-colors ${
                                  bulkCopyDateFilter === f.key
                                    ? 'bg-blue-500 text-white border-blue-500'
                                    : darkMode ? 'text-gray-400 border-gray-600 hover:border-gray-500' : 'text-gray-500 border-gray-300 hover:border-gray-400'
                                }`}
                              >
                                {f.label}
                              </button>
                            ))}
                            {/* Symbol filter dropdown (only if multiple symbols) */}
                            {symbols.length > 1 && (
                              <div className="ml-auto min-w-[110px]">
                                <GlassSelect
                                  value={bulkCopySymbolFilter}
                                  onChange={(v) => { setBulkCopySymbolFilter(v); setBulkCopySelectedTrades([]); }}
                                  options={[
                                    { value: 'all', label: 'All Symbols' },
                                    ...symbols.map(s => ({ value: s, label: s })),
                                  ]}
                                  darkMode={darkMode}
                                  size="sm"
                                />
                              </div>
                            )}
                          </div>

                          {/* Trade picker */}
                          <div className={`max-h-48 overflow-y-auto rounded-lg border mb-3 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                            {/* Select All / Deselect All */}
                            <div className={`px-3 py-1.5 text-xs border-b flex gap-2 items-center ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-200'}`}>
                              <button
                                type="button"
                                onClick={() => setBulkCopySelectedTrades(filteredBulkTrades.map(t => t.id))}
                                className={darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-600'}
                              >
                                Select All
                              </button>
                              <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>|</span>
                              <button
                                type="button"
                                onClick={() => setBulkCopySelectedTrades([])}
                                className={darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-600'}
                              >
                                Deselect All
                              </button>
                              <span className={`ml-auto ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {filteredBulkTrades.length} trade{filteredBulkTrades.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            {filteredBulkTrades.length === 0 ? (
                              <div className={`px-3 py-4 text-xs text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                No trades match the current filters
                              </div>
                            ) : (
                              filteredBulkTrades.map(t => {
                                const isChecked = bulkCopySelectedTrades.includes(t.id);
                                const acct = accounts.find(a => a.id === t.accountId);
                                return (
                                  <label
                                    key={t.id}
                                    className={`flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer transition-colors ${
                                      isChecked
                                        ? darkMode ? 'bg-blue-900/30' : 'bg-blue-50'
                                        : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => {
                                        setBulkCopySelectedTrades(prev =>
                                          isChecked ? prev.filter(id => id !== t.id) : [...prev, t.id]
                                        );
                                      }}
                                      className="rounded-sm"
                                    />
                                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                                      {t.date}{t.entryTime ? ` ${t.entryTime}` : ''} — {t.symbol} {t.direction === 'short' ? '▼' : '▲'} {t.setup || 'No Setup'}
                                    </span>
                                    {acct && (
                                      <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-medium ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                                        {acct.name || acct.id}
                                      </span>
                                    )}
                                    <span className={`ml-auto whitespace-nowrap ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                      {(t.confluences || []).length}{(t.counterConfluences || []).length > 0 ? `/${(t.counterConfluences || []).length}` : ''} conf
                                    </span>
                                  </label>
                                );
                              })
                            )}
                          </div>
                        </>
                      );
                    })()}

                    {/* Actions */}
                    <div className="flex gap-2 justify-end">
                      <button
                        type="button"
                        onClick={() => setShowBulkCopy(false)}
                        className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                          darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={bulkCopySelectedTrades.length === 0}
                        onClick={() => {
                          // Strip the source setup's implied confluences before
                          // sending — they're setup-definitional (auto-applied
                          // by applyImpliedConfluences on save), not user-chosen,
                          // so they shouldn't propagate to a target trade whose
                          // setup may differ. Mirrors the JournalView copy flow.
                          const srcImplied = Array.isArray(setupDetails?.[trade.setup]?.impliedConfluences)
                            ? setupDetails[trade.setup].impliedConfluences
                            : [];
                          const filteredConf = (trade.confluences || []).filter(c => !srcImplied.includes(c));
                          onBulkCopyConfluences(filteredConf, bulkCopySelectedTrades, bulkCopyMode, trade.counterConfluences || []);
                          setShowBulkCopy(false);
                        }}
                        className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
                      >
                        <Check size={14} />
                        Apply to {bulkCopySelectedTrades.length} Trade{bulkCopySelectedTrades.length !== 1 ? 's' : ''}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons — frosted-glass: neutral Cancel + green Save CTA */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg border transition-[background-color,border-color,box-shadow,color] duration-150 ${
              darkMode
                ? 'bg-white/[0.04] border-white/10 text-gray-300 hover:bg-white/[0.08] hover:border-white/20 hover:text-white'
                : 'bg-white/60 border-gray-200 text-gray-700 hover:bg-white/80 hover:border-gray-300 hover:text-gray-900'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={!trade.symbol || (!trade.entry && !trade.pnl)}
            className={`px-4 py-2 rounded-lg border transition-[background-color,border-color,box-shadow,color] duration-150 disabled:opacity-40 disabled:cursor-not-allowed ${
              darkMode
                ? 'bg-green-500/25 border-green-400/50 text-green-50 ring-1 ring-inset ring-green-400/25 hover:bg-green-500/35 hover:border-green-400/70 hover:shadow-[0_0_18px_rgba(34,197,94,0.3)]'
                : 'bg-green-100 border-green-300 text-green-800 hover:bg-green-200 hover:border-green-400'
            }`}
          >
            {isEditing ? 'Save Changes' : 'Add Trade'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditTradeModal;
